import { Button } from 'antd';
import React, { useMemo, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { deviceAdminApi } from '../../../../api';
import { ComponentPermission, getPermissionErrorMessage, SecuredComponents, User } from '../../../../model/AccountModel';
import { PaginationSetting, ResponseObject } from '../../../../model/CommonModel';
import { CurrentDeviceControlObj, DeviceObjectType, Reader, ReaderError, ReaderInterface } from '../../../../model/DeviceAdminModel';
import { useStoreSelector } from '../../../../store';
import { setCurrentDevice } from '../../../../store/common/actions';
import { setControllerContacts } from '../../../../store/deviceControl/actions';
import { selectDigitracFilterMode, selectTablePaginationSetting } from '../../../../store/deviceControl/selectors';
import { InformationMessages, Modal, ModalWarning, WithLockedValidation } from '../../../common';
import { NotificationStatus } from '../../../common/NotificationStatus/NotificationStatus';
import { readerContext, readerState, StoreContext } from './contextReader';
import { ReaderContainer } from './ReaderContainer/ReaderContainer';

const user: User = getUser();
const readerPermissions: ComponentPermission = User.getComponentPermission(user, SecuredComponents.Reader);

type Props = {
	id: number;
	address: string;
	setShouldResetSearchColumn: () => void;
	cancelHandle?: (device: CurrentDeviceControlObj) => void;
	setRedirectPage?: () => void;
	saveCallBack?: () => void;
};

const ReaderModal: React.FC<Props> = WithLockedValidation(({ id, address, setShouldResetSearchColumn, cancelHandle, saveCallBack, setRedirectPage }) => {
	const dispatch = useDispatch();
	const [stateContext, dispatchActionContext] = useReducer(readerContext, readerState);
	const [isLoading, setLoading] = useState<boolean>(true);
	const [errorType, setErrorType] = useState<ReaderError>(undefined);
	const [errorMessage, setErrorMessage] = useState('');
	const [downloadedFirmware, setDownloadedFirmware] = useState<boolean>(false);
	const [submittedForm, setSubmittedForm] = useState<boolean>(false);

	const {
		name,
		readerInterface,
		index,
		supportedReaderClass,
		oSDPAddress,
		wLHubAddress,
		eACAddress,
		wL2HubAddress,
		allegionLockAddress,
		okDegrade,
		antiPassback,
		cCOTZAssurance,
		firmware,
		enableReaderBezelTamper,
		enableHexPassThrough,
		enableMatchBezelTamper,
		facilityCodeCardNumber,
		corporate1000,
		hexDigitUUID,
		enableKeyPad,
		genericTimeZones,
		matchAlgorithm,
		fascnHandling,
		anyReaderOptions,
		cardReaderOptions,
		scramblePadKeyPadOptions,
		controllerId,
		selections: {
			cardMap,
			specialHandlingInstruction,
			readerThreatLevel,
			toZone,
			fromZone,
			defaultAssuranceLevel,
			cCOTZThreatLevel,
			assuranceLevel,
			cardCodeOnlyTimeZone,
			rS485ReaderType,
		},
		unpermitted,
		isDoorWireless,
		osdpLEDOptions,
		useCustomOSDPLEDColor,
		changedOSDPLEDColor,
	} = stateContext;

	const isFilterMode: boolean = useStoreSelector<boolean>(selectDigitracFilterMode);
	const tablePaginationSetting: PaginationSetting = useStoreSelector<PaginationSetting>(selectTablePaginationSetting);

	const shouldResetSearchColumn = () => {
		if (setRedirectPage) {
			setRedirectPage();
		}
		if (isFilterMode) {
			setShouldResetSearchColumn();
		}
	};

	const handleEditReader = async () => {
		setSubmittedForm(true);

		const reader: Partial<Reader> = {
			ControllerId: controllerId,
			Name: name,
			Id: id,
			Index: index,
			SupportedReaderClass: supportedReaderClass,
			CardMap: cardMap,
			RS485Interface: readerInterface === ReaderInterface.RS485,
			AnyReaderOptions: anyReaderOptions,
			CardReaderOptions: cardReaderOptions,
			ScramblePadKeyPadOptions: scramblePadKeyPadOptions,
			DisabledReaderAboveLevelId: readerThreatLevel,
			DisabledCCOTZAboveLevelId: cCOTZThreatLevel,
			OSDPAddress: oSDPAddress,
			WLHubAddress: wLHubAddress,
			EACAddress: eACAddress,
			WL2HubAddress: wL2HubAddress,
			AllegionLockAddress: allegionLockAddress,
			OkDegrade: okDegrade,
			AntiPassback: antiPassback,
			CCOTZAssurance: cCOTZAssurance,
			AssuranceLevel: assuranceLevel,
			DefaultAssuranceLevel: defaultAssuranceLevel,
			Firmware: firmware,
			ToZone: toZone,
			FromZone: fromZone,
			EnableReaderBezelTamper: enableReaderBezelTamper,
			EnableHexPassThrough: enableHexPassThrough,
			SpecialHandlingInstruction: specialHandlingInstruction,
			EnableMatchBezelTamper: enableMatchBezelTamper,
			FacilityCodeCardNumber: facilityCodeCardNumber,
			Corporate1000: corporate1000,
			HexDigitUUID: hexDigitUUID,
			EnableKeyPad: enableKeyPad,
			CardCodeOnlyTimeZone: unpermitted.cardCodeOnlyTimeZone
				? unpermitted.cardCodeOnlyTimeZone
				: genericTimeZones.find(x => x.GenericId === cardCodeOnlyTimeZone),
			RS485ReaderType: rS485ReaderType,
			ReaderInterface: readerInterface,
			MatchAlgorithm: matchAlgorithm,
			FascnHandling: fascnHandling,
			IsDoorWireless: isDoorWireless,
			ChangedOSDPLEDColor: changedOSDPLEDColor,
			OSDPLEDOptions: osdpLEDOptions,
			UseCustomOSDPLEDColor: useCustomOSDPLEDColor,
		};

		const response: ResponseObject = await deviceAdminApi.editReader(reader);

		const paginationSetting = isFilterMode ? ({ ...tablePaginationSetting, SearchedValue: '' } as PaginationSetting) : tablePaginationSetting;

		NotificationStatus({
			responseData: response,
			onSuccessCallback: () => {
				dispatch(setControllerContacts(DeviceObjectType.Reader, paginationSetting));
				handleCancelModal();
				shouldResetSearchColumn();
				if (saveCallBack) {
					saveCallBack();
				}
			},
			onAdditionalInfoRequiredCallback: () => {
				const messages: string[] = response.AdditionalResponseInfo.split('\n');

				const content: JSX.Element = <InformationMessages messages={messages} />;
				ModalWarning({
					title: _('FailedToSyncTimeZone'),
					content: content,
					onOk: () => handleCancelModal(),
					onCancel: () => handleCancelModal(),
					okText: _('Ok'),
					width: '700px',
				});
				setSubmittedForm(false);
			},
			onFailedValidation: () => {
				setErrorMessage(response.ErrorMessage);
				if (errorType !== ReaderError.EACAddress) {
					setErrorType(undefined);
				}
				setErrorType(ReaderError[response.AdditionalResponseInfo]);
				setSubmittedForm(false);
			},
		});
	};

	const handleCloseModal = () => {
		const newDevice: CurrentDeviceControlObj = { Id: 0, DeviceObjectType: DeviceObjectType.Reader, IsModalOpen: false };
		if (cancelHandle) {
			cancelHandle({ Id: 0, DeviceObjectType: DeviceObjectType.Reader });
		} else {
			dispatch(setCurrentDevice(newDevice));
		}
	};

	const handleCancelModal = () => {
		deviceAdminApi.unlockComponentDeviceAdmin(id, SecuredComponents.Reader);
		handleCloseModal();
	};

	const handleDownloadFirmware = () => {
		setDownloadedFirmware(true);
	};

	const contextValue = useMemo(() => {
		return { readerState: stateContext, dispatcher: dispatchActionContext };
	}, [stateContext, dispatchActionContext]);

	return (
		<StoreContext.Provider value={contextValue}>
			<Modal
				onClickOk={() => null}
				onCancel={handleCancelModal}
				title={`${_('EditReader')} ${address}`}
				visible={true}
				footer={[
					<Button
						id='readerSaveChangesButton'
						key='save'
						type='primary'
						onClick={handleEditReader}
						title={getPermissionErrorMessage(readerPermissions.canUpdate)}
						disabled={isLoading || !readerPermissions.canUpdate || cardCodeOnlyTimeZone === 0 || downloadedFirmware}>
						{_('SaveChanges')}
					</Button>,
					<Button id='readerCancelButton' key='cancel' onClick={handleCancelModal}>
						{_('Cancel')}
					</Button>,
				]}
				width='800px'>
				<div>
					<ReaderContainer
						submittedForm={submittedForm}
						handleDownloadFirmware={handleDownloadFirmware}
						errorType={errorType}
						errorMessage={errorMessage}
						id={id}
						setLoading={setLoading}
						isLoading={isLoading}
						useDoorContext={false}
					/>
				</div>
			</Modal>
		</StoreContext.Provider>
	);
});

export { ReaderModal };
